import React from 'react'
import TextField from '@mui/material/TextField'
import {observable, computed, makeObservable} from 'mobx'
import {ColDef} from 'ag-grid-community'
import {ValidationResult, ValidationState, Validator} from './validation'
import {makeStyles} from '@oc/styles'
import {observer} from 'mobx-react-lite'

const useStyles = makeStyles(theme => ({
    editorRoot: {
        background: 'white',
        padding: theme.spacing(0, 1)
    }
}))

type ChangeEvent = React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>

export interface ValidatedTextEditorProps {
    // properties from CustomCellEditorProps
    value: any
    data: any
    colDef: ColDef
    onValueChange?: (newValue: any) => void

    // custom properties
    showLabel?: boolean
    label?: string
    id?: string
    validator: Validator
    directSave?: boolean
    existingValues?: (exclude: string) => string[]
}

class ValidatedTextEditorState {
    @observable private _validationState = ValidationState.OK
    @observable private _errorText = ''
    @observable private _errorVisible = false
    @observable private _currentValue = ''

    @computed
    public get validationState(): ValidationState {
        return this._validationState
    }
    public set validationState(v: ValidationState) {
        this._validationState = v
    }

    @computed
    public get errorText(): string {
        return this._errorText
    }
    public set errorText(v: string) {
        this._errorText = v
    }

    @computed
    public get errorVisible(): boolean {
        return this._errorVisible
    }
    public set errorVisible(v: boolean) {
        this._errorVisible = v
    }

    @computed
    public get currentValue(): string {
        return this._currentValue
    }
    public set currentValue(v: string) {
        this._currentValue = v
    }

    public constructor(currentValue: string, validationResult: ValidationResult) {
        this.currentValue = currentValue
        if (validationResult.error === ValidationState.Error || validationResult.error === ValidationState.Warning) {
            this.errorText = validationResult.message
            this.errorVisible = true
            this.validationState = validationResult.error
        } else if (validationResult.error === ValidationState.OK) {
            this.errorText = ''
            this.errorVisible = false
            this.validationState = ValidationState.OK
        }
        makeObservable(this)
    }
}

function _ValidatedTextEditor(props: ValidatedTextEditorProps) {
    const {classes} = useStyles()
    const [state] = React.useState(() => new ValidatedTextEditorState(props.value, props.validator(props.value, null)))
    const inputRef = React.useRef<HTMLInputElement>(null)

    const onChange = React.useCallback(
        (e: ChangeEvent) => {
            const valueToSave = e.target.value
            state.currentValue = valueToSave
            if (props.validator) {
                let validationResult
                if (props.existingValues) {
                    validationResult = props.validator(valueToSave, props.existingValues(props.value))
                } else {
                    validationResult = props.validator(valueToSave, null)
                }
                if (
                    validationResult.error === ValidationState.Error ||
                    validationResult.error === ValidationState.Warning
                ) {
                    state.errorText = validationResult.message
                    state.errorVisible = true
                    state.validationState = validationResult.error
                } else if (validationResult.error === ValidationState.OK) {
                    state.errorText = ''
                    state.errorVisible = false
                    state.validationState = ValidationState.OK
                }
                if (props.directSave && props.colDef.field) {
                    props.data[props.colDef.field] = valueToSave
                }
                props.onValueChange?.(valueToSave)
            }
        },
        [props, state]
    )

    React.useEffect(() => {
        inputRef.current?.focus()
        inputRef.current?.select()
    }, [])

    const showLabel = props.showLabel !== false
    const showError =
        state.errorVisible && !!props.colDef.field && typeof props.data[props.colDef.field] !== 'undefined'

    return (
        <div className={classes.editorRoot}>
            <TextField
                error={showError}
                fullWidth
                onChange={onChange}
                helperText={showError ? state.errorText : '\xa0'}
                value={state.currentValue}
                label={showLabel === false ? '\xa0' : props.label || '\xa0'}
                id={props.id}
                inputRef={inputRef}
            />
        </div>
    )
}

export const ValidatedTextEditor = observer(_ValidatedTextEditor)
